import { cn } from '@utils/cn';
import React from 'react';
import {
  ProjectStatus,
  getDisplayProjectStatus,
} from '@models/projectStatus.model';
import { useTranslation } from '@hooks/useTranslation';

interface ProjectStatusBadgeProps {
  state?: string | null;
  isBehind?: boolean | null;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'solid' | 'soft';
  className?: string;
}

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs h-5',
  md: 'px-3 py-1 text-sm h-6',
  lg: 'px-4 py-1.5 text-base h-8',
};

const solidByStatus: Record<string, string> = {
  [ProjectStatus.ONGOING]: 'bg-ongoing-project text-white',
  [ProjectStatus.COMPLETED]: 'bg-complete-project text-white',
  [ProjectStatus.ABANDONED]: 'bg-abandoned-project text-white',
  [ProjectStatus.DELAYED]: 'bg-delayed-project text-white',
  [ProjectStatus.BIDDING]: 'bg-bidding-project text-white',
  [ProjectStatus.DRAFT]: 'bg-draft-project text-white',
  [ProjectStatus.BEHIND]: 'bg-behind-project text-white',
};

const softByStatus: Record<string, string> = {
  [ProjectStatus.ONGOING]: 'bg-green-100 text-green-800',
  [ProjectStatus.COMPLETED]: 'bg-emerald-100 text-emerald-800',
  [ProjectStatus.ABANDONED]: 'bg-red-100 text-red-800',
  [ProjectStatus.DELAYED]: 'bg-amber-100 text-amber-800',
  [ProjectStatus.BIDDING]: 'bg-indigo-100 text-indigo-800',
  [ProjectStatus.DRAFT]: 'bg-slate-100 text-slate-800',
  [ProjectStatus.BEHIND]: 'bg-orange-100 text-orange-800',
};

export const ProjectStatusBadge: React.FC<ProjectStatusBadgeProps> = ({
  state,
  isBehind,
  size = 'sm',
  variant = 'solid',
  className = '',
}) => {
  // Sprint 28, story-144. `projects.*` — the single render call site for
  // `getDisplayProjectStatus`'s label (docs/architecture.md "## Sprint 28" Decision 1 / house
  // style §5), same approach as mobile's `ProjectStatusBadge`.
  const tProjects = useTranslation('projects');
  const displayStatus = getDisplayProjectStatus(state, isBehind);
  const colorMap = variant === 'soft' ? softByStatus : solidByStatus;
  const colorClass = colorMap[displayStatus] || 'bg-gray-500 text-white';
  const baseClasses =
    'inline-flex items-center justify-center rounded-full font-medium whitespace-nowrap w-fit capitalize';
  const labelByStatus: Record<string, string> = {
    [ProjectStatus.ONGOING]: tProjects.statusOngoing,
    [ProjectStatus.COMPLETED]: tProjects.statusCompleted,
    [ProjectStatus.ABANDONED]: tProjects.statusAbandoned,
    [ProjectStatus.DELAYED]: tProjects.statusDelayed,
    [ProjectStatus.BIDDING]: tProjects.statusBidding,
    [ProjectStatus.DRAFT]: tProjects.statusDraft,
    [ProjectStatus.BEHIND]: tProjects.statusBehind,
  };

  return (
    <span
      className={cn(baseClasses, sizeClasses[size], colorClass, className)}>
      {labelByStatus[displayStatus] ?? displayStatus}
    </span>
  );
};
